import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { DeckShell } from './DeckShell';
import { BarChart3Icon, TargetIcon, ActivityIcon, SparklesIcon, LayoutDashboardIcon, FileTextIcon, ArrowRightIcon, CheckCircle2Icon, Building2Icon, Globe2Icon, UsersIcon } from 'lucide-react';
const fadeUp = {
  initial: {
    opacity: 0,
    y: 24,
    filter: 'blur(8px)'
  },
  animate: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)'
  },
  transition: {
    duration: 0.7,
    ease: [0.22, 1, 0.36, 1]
  }
};
const scaleIn = {
  initial: {
    opacity: 0,
    scale: 0.92
  },
  animate: {
    opacity: 1,
    scale: 1
  },
  transition: {
    duration: 0.8,
    ease: [0.22, 1, 0.36, 1]
  }
};
const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1
    }
  }
};
export function InvestorDeckPage() {
  const slides = [
  // 1) TITLE
  <div key="1" className="flex-1 flex flex-col items-center justify-center relative px-6 overflow-hidden" data-id="element-1912">
      <motion.div animate={{
      scale: [1, 1.15, 1],
      opacity: [0.4, 0.7, 0.4]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(14,165,164,0.18)_0%,transparent_55%)] pointer-events-none" data-id="element-1913" />
      <motion.div animate={{
      scale: [1, 1.2, 1],
      opacity: [0.2, 0.45, 0.2]
    }} transition={{
      duration: 10,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 2
    }} className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(255,107,74,0.15)_0%,transparent_45%)] pointer-events-none" data-id="element-1914" />
      <motion.div variants={stagger} initial="initial" animate="animate" className="text-center z-10" data-id="element-1915">
        <motion.h1 variants={{
        initial: {
          opacity: 0,
          scale: 0.85,
          filter: 'blur(20px)'
        },
        animate: {
          opacity: 1,
          scale: 1,
          filter: 'blur(0px)'
        }
      }} transition={{
        duration: 1.2,
        ease: [0.22, 1, 0.36, 1]
      }} className="font-serif-display text-6xl md:text-8xl font-bold mb-6 tracking-tight" data-id="element-1916">
          Offsite<span className="text-brand-coral" data-id="element-1917">+</span>
        </motion.h1>
        <motion.p variants={fadeUp} className="text-xl md:text-3xl text-slate-300 font-light mb-16 max-w-2xl mx-auto leading-relaxed" data-id="element-1918">
          The AI experience layer for every group event.
        </motion.p>
        <motion.div variants={fadeUp} className="flex items-center justify-center gap-4 text-xs font-bold text-slate-500 uppercase tracking-[0.2em]" data-id="element-1919">
          <span data-id="element-1920">Seed Round</span>
          <span className="w-1 h-1 rounded-full bg-slate-700" data-id="element-1921" />
          <span data-id="element-1922">2026</span>
          <span className="w-1 h-1 rounded-full bg-slate-700" data-id="element-1923" />
          <span data-id="element-1924">Confidential</span>
        </motion.div>
      </motion.div>
    </div>,
  // 2) THE PROBLEM
  <div key="2" className="flex-1 flex flex-col justify-center px-8 md:px-20 max-w-6xl mx-auto w-full" data-id="element-1925">
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-1926">
        <motion.div variants={fadeUp} className="text-brand-coral text-sm font-bold uppercase tracking-[0.2em] mb-4" data-id="element-1927">
          The Problem
        </motion.div>
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-6xl font-bold mb-16 leading-tight max-w-4xl" data-id="element-1928">
          Group events are still designed in spreadsheets.
        </motion.h2>
        <div className="grid md:grid-cols-3 gap-8" data-id="element-1929">
          {[{
          stat: '$840B',
          text: 'spent on group events globally'
        }, {
          stat: '74%',
          text: 'of leaders say their last offsite was forgettable'
        }, {
          stat: '<8%',
          text: 'of organizers can show measurable ROI'
        }].map((item, i) => <motion.div key={i} variants={fadeUp} className="bg-white/5 border border-white/10 rounded-2xl p-8 backdrop-blur-sm" data-id="element-1930">
              <div className="font-serif-display text-5xl font-bold text-brand-teal mb-4" data-id="element-1931">
                {item.stat}
              </div>
              <div className="text-lg text-slate-300 leading-relaxed" data-id="element-1932">
                {item.text}
              </div>
            </motion.div>)}
        </div>
      </motion.div>
    </div>,
  // 3) THE INSIGHT
  <div key="3" className="flex-1 flex flex-col justify-center px-8 md:px-20 max-w-6xl mx-auto w-full" data-id="element-1933">
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-1934">
        <motion.div variants={fadeUp} className="text-brand-coral text-sm font-bold uppercase tracking-[0.2em] mb-4" data-id="element-1935">
          The Insight
        </motion.div>
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-5xl font-bold mb-16 leading-tight max-w-4xl" data-id="element-1936">
          Every group event is a media moment, an alignment moment, and a data
          moment — but no one captures all three.
        </motion.h2>
        <div className="grid md:grid-cols-3 gap-12 relative" data-id="element-1937">
          <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-y-1/2 hidden md:block" data-id="element-1938" />
          {[{
          icon: ActivityIcon,
          title: 'Media',
          desc: 'The branded experience before, during, and after.'
        }, {
          icon: TargetIcon,
          title: 'Alignment',
          desc: 'The content and frameworks that drive outcomes.'
        }, {
          icon: BarChart3Icon,
          title: 'Data',
          desc: 'The measurable ROI and commitments generated.'
        }].map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={i} variants={fadeUp} className="relative z-10 bg-[#0A0F1F] p-6 text-center" data-id="element-1939">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-6 text-brand-teal" data-id="element-1940">
                  <Icon className="w-8 h-8" data-id="element-1941" />
                </div>
                <h3 className="text-2xl font-bold mb-3" data-id="element-1942">{item.title}</h3>
                <p className="text-slate-400" data-id="element-1943">{item.desc}</p>
              </motion.div>;
        })}
        </div>
      </motion.div>
    </div>,
  // 4) THE PRODUCT
  <div key="4" className="flex-1 flex flex-col justify-center px-8 md:px-20 max-w-6xl mx-auto w-full" data-id="element-1944">
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-1945">
        <motion.div variants={fadeUp} className="text-brand-coral text-sm font-bold uppercase tracking-[0.2em] mb-4" data-id="element-1946">
          The Product
        </motion.div>
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-5xl font-bold mb-16 leading-tight" data-id="element-1947">
          One AI-native platform from intake to insights.
        </motion.h2>
        <div className="grid md:grid-cols-3 gap-6" data-id="element-1948">
          {[{
          icon: SparklesIcon,
          title: 'AI Designer',
          desc: 'Generate a world-class agenda in minutes.',
          mock: <div className="bg-slate-900/60 border border-white/10 rounded-lg p-3 space-y-2" data-id="element-1949">
                  <div className="flex gap-1.5" data-id="element-1950">
                    <div className="px-2 py-0.5 rounded-full bg-brand-teal/20 text-brand-teal text-[9px] font-bold" data-id="element-1951">
                      Leadership
                    </div>
                    <div className="px-2 py-0.5 rounded-full bg-brand-coral/20 text-brand-coral text-[9px] font-bold" data-id="element-1952">
                      Recovery
                    </div>
                    <div className="px-2 py-0.5 rounded-full bg-white/10 text-white/60 text-[9px] font-bold" data-id="element-1953">
                      Bonding
                    </div>
                  </div>
                  <div className="space-y-1.5" data-id="element-1954">
                    <div className="flex items-center gap-2" data-id="element-1955">
                      <div className="w-8 text-[9px] text-slate-500 font-mono" data-id="element-1956">
                        09:00
                      </div>
                      <div className="flex-1 h-2 bg-brand-teal/40 rounded-full" data-id="element-1957" />
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1958">
                      <div className="w-8 text-[9px] text-slate-500 font-mono" data-id="element-1959">
                        11:30
                      </div>
                      <div className="flex-1 h-2 bg-white/15 rounded-full" data-id="element-1960" />
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1961">
                      <div className="w-8 text-[9px] text-slate-500 font-mono" data-id="element-1962">
                        14:00
                      </div>
                      <div className="flex-1 h-2 bg-brand-coral/40 rounded-full" data-id="element-1963" />
                    </div>
                  </div>
                </div>
        }, {
          icon: LayoutDashboardIcon,
          title: 'Live Event Hub',
          desc: 'The branded companion app for attendees.',
          mock: <div className="bg-slate-900/60 border border-white/10 rounded-lg p-3" data-id="element-1964">
                  <div className="flex items-center justify-between mb-2" data-id="element-1965">
                    <div className="text-[9px] font-bold text-white/80" data-id="element-1966">
                      Today
                    </div>
                    <div className="flex items-center gap-1" data-id="element-1967">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-1968" />
                      <span className="text-[8px] font-bold text-emerald-400 tracking-wider" data-id="element-1969">
                        LIVE
                      </span>
                    </div>
                  </div>
                  <div className="rounded bg-brand-coral/20 border border-brand-coral/40 px-2 py-1.5 mb-1.5" data-id="element-1970">
                    <div className="text-[9px] font-bold text-white" data-id="element-1971">
                      Alignment Workshop
                    </div>
                    <div className="text-[8px] text-brand-coral" data-id="element-1972">
                      14:30 · Ballroom A
                    </div>
                  </div>
                  <div className="rounded bg-white/5 border border-white/10 px-2 py-1.5" data-id="element-1973">
                    <div className="text-[9px] font-bold text-white/70" data-id="element-1974">
                      Commitment Circle
                    </div>
                    <div className="text-[8px] text-white/40" data-id="element-1975">16:30 · next</div>
                  </div>
                </div>
        }, {
          icon: FileTextIcon,
          title: 'Outcomes Report',
          desc: 'Automated ROI and engagement metrics.',
          mock: <div className="bg-slate-900/60 border border-white/10 rounded-lg p-3" data-id="element-1976">
                  <div className="flex items-center justify-between mb-2" data-id="element-1977">
                    <div className="text-[9px] font-bold text-white/80" data-id="element-1978">
                      Engagement
                    </div>
                    <div className="text-[10px] font-bold text-emerald-400" data-id="element-1979">
                      +94%
                    </div>
                  </div>
                  <div className="flex items-end gap-1 h-10" data-id="element-1980">
                    {[35, 55, 45, 70, 60, 85, 100].map((h, i) => <div key={i} className="flex-1 bg-brand-teal/70 rounded-t" style={{
                height: `${h}%`
              }} data-id="element-1981" />)}
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/10" data-id="element-1982">
                    <div className="text-[8px] text-slate-500" data-id="element-1983">NPS</div>
                    <div className="text-[9px] font-bold text-white" data-id="element-1984">94</div>
                  </div>
                </div>
        }].map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={i} variants={fadeUp} className="bg-white/5 border border-white/10 rounded-2xl p-8 flex flex-col" data-id="element-1985">
                <Icon className="w-8 h-8 text-white mb-6" data-id="element-1986" />
                <h3 className="text-xl font-bold mb-2" data-id="element-1987">{item.title}</h3>
                <p className="text-slate-400 mb-6 flex-1" data-id="element-1988">{item.desc}</p>
                {item.mock}
              </motion.div>;
        })}
        </div>
      </motion.div>
    </div>,
  // 5) DEMO SLIDE — AI in action
  <div key="5" className="flex-1 flex flex-col justify-center items-center px-8 md:px-20 max-w-6xl mx-auto w-full relative overflow-hidden" data-id="element-1989">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,107,74,0.15)_0%,transparent_60%)] pointer-events-none" data-id="element-1990" />
      <motion.div variants={stagger} initial="initial" animate="animate" className="w-full text-center z-10" data-id="element-1991">
        <motion.h2 variants={fadeUp} className="font-serif-display text-5xl font-bold mb-3" data-id="element-1992">
          See it live.
        </motion.h2>
        <motion.p variants={fadeUp} className="text-slate-400 mb-8 text-base" data-id="element-1993">
          The AI predicts, proposes, and follows up — automatically.
        </motion.p>
        <motion.div variants={fadeUp} className="relative mx-auto w-full max-w-5xl bg-slate-900 rounded-xl border border-slate-700 shadow-2xl overflow-hidden" data-id="element-1994">
          {/* Browser chrome */}
          <div className="h-8 bg-slate-800 border-b border-slate-700 flex items-center px-4 gap-2" data-id="element-1995">
            <div className="w-2.5 h-2.5 rounded-full bg-rose-500" data-id="element-1996" />
            <div className="w-2.5 h-2.5 rounded-full bg-amber-500" data-id="element-1997" />
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" data-id="element-1998" />
            <div className="flex-1 flex justify-center" data-id="element-1999">
              <div className="text-[10px] text-slate-500 font-mono" data-id="element-2000">
                offsiteplus.com/partner/lamplighter/pipeline
              </div>
            </div>
          </div>
          {/* App body */}
          <div className="p-4 grid grid-cols-3 gap-3 h-[380px] text-left" data-id="element-2001">
            {/* Panel 1: Booking Book — AI Predictions */}
            <motion.div variants={fadeUp} className="bg-slate-800/60 rounded-lg border border-slate-700 p-3 flex flex-col" data-id="element-2002">
              <div className="flex items-center justify-between mb-3" data-id="element-2003">
                <div className="text-[10px] font-bold text-white" data-id="element-2004">
                  Booking Book
                </div>
                <div className="flex items-center gap-1" data-id="element-2005">
                  <div className="w-1 h-1 rounded-full bg-brand-teal animate-pulse" data-id="element-2006" />
                  <span className="text-[8px] font-bold text-brand-teal tracking-wider" data-id="element-2007">
                    AI SCAN
                  </span>
                </div>
              </div>
              <div className="space-y-2 flex-1" data-id="element-2008">
                {[{
                name: 'Cedarwood Group',
                date: 'Aug 14–16',
                rooms: '32 rooms',
                prob: 92,
                top: true
              }, {
                name: 'Meridian Health',
                date: 'Sep 03–05',
                rooms: '24 rooms',
                prob: 78
              }, {
                name: 'Pinecrest Capital',
                date: 'Sep 22–23',
                rooms: '18 rooms',
                prob: 64
              }, {
                name: 'Vela Robotics',
                date: 'Oct 11–13',
                rooms: '40 rooms',
                prob: 41
              }].map((g, i) => <div key={i} className={`rounded p-2 border ${g.top ? 'bg-brand-teal/10 border-brand-teal/40' : 'bg-slate-900/40 border-slate-700/50'}`} data-id="element-2009">
                    <div className="flex items-center justify-between mb-1" data-id="element-2010">
                      <div className="text-[10px] font-bold text-white truncate" data-id="element-2011">
                        {g.name}
                      </div>
                      <div className={`text-[9px] font-bold ${g.prob >= 80 ? 'text-brand-teal' : g.prob >= 60 ? 'text-amber-400' : 'text-slate-500'}`} data-id="element-2012">
                        {g.prob}%
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-[8px] text-slate-400 mb-1.5" data-id="element-2013">
                      <span data-id="element-2014">{g.date}</span>
                      <span data-id="element-2015">·</span>
                      <span data-id="element-2016">{g.rooms}</span>
                    </div>
                    <div className="h-1 bg-slate-700 rounded-full overflow-hidden" data-id="element-2017">
                      <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: `${g.prob}%`
                  }} transition={{
                    duration: 1,
                    delay: 0.6 + i * 0.15,
                    ease: [0.22, 1, 0.36, 1]
                  }} className={`h-full rounded-full ${g.prob >= 80 ? 'bg-brand-teal' : g.prob >= 60 ? 'bg-amber-400' : 'bg-slate-500'}`} data-id="element-2018" />
                    </div>
                  </div>)}
              </div>
            </motion.div>

            {/* Panel 2: Auto-generated Proposal */}
            <motion.div variants={fadeUp} className="bg-slate-800/60 rounded-lg border border-slate-700 p-3 flex flex-col" data-id="element-2019">
              <div className="flex items-center justify-between mb-3" data-id="element-2020">
                <div className="text-[10px] font-bold text-white" data-id="element-2021">
                  AI Proposal Draft
                </div>
                <div className="flex items-center gap-1" data-id="element-2022">
                  <motion.div animate={{
                  opacity: [1, 0.3, 1]
                }} transition={{
                  duration: 1.4,
                  repeat: Infinity
                }} className="w-1 h-1 rounded-full bg-brand-coral" data-id="element-2023" />
                  <span className="text-[8px] font-bold text-brand-coral tracking-wider" data-id="element-2024">
                    GENERATING
                  </span>
                </div>
              </div>
              <div className="bg-slate-900/60 rounded p-3 flex-1 flex flex-col space-y-2 text-left" data-id="element-2025">
                <div className="text-[8px] font-bold text-brand-coral tracking-widest" data-id="element-2026">
                  PREMIUM UPGRADE
                </div>
                <div className="text-[11px] font-serif-display font-bold text-white leading-tight" data-id="element-2027">
                  Executive Alignment Retreat at Lamplighter Inn
                </div>
                <div className="text-[8px] text-slate-400" data-id="element-2028">
                  Prepared for Cedarwood Group · Aug 14–16
                </div>
                <div className="space-y-1 pt-2 border-t border-slate-700" data-id="element-2029">
                  <div className="flex items-center justify-between" data-id="element-2030">
                    <div className="text-[9px] text-slate-300" data-id="element-2031">
                      Branded retreat hub
                    </div>
                    <div className="text-[9px] text-white font-bold" data-id="element-2032">
                      $2,400
                    </div>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-2033">
                    <div className="text-[9px] text-slate-300" data-id="element-2034">
                      Leadership module x2
                    </div>
                    <div className="text-[9px] text-white font-bold" data-id="element-2035">
                      $8,800
                    </div>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-2036">
                    <div className="text-[9px] text-slate-300" data-id="element-2037">
                      Outcomes report
                    </div>
                    <div className="text-[9px] text-white font-bold" data-id="element-2038">
                      $1,500
                    </div>
                  </div>
                  <div className="flex items-center gap-1 pt-1" data-id="element-2039">
                    <span className="text-[9px] text-slate-500" data-id="element-2040">
                      F&amp;B upgrades
                    </span>
                    <motion.span animate={{
                    opacity: [1, 0, 1]
                  }} transition={{
                    duration: 0.8,
                    repeat: Infinity
                  }} className="inline-block w-0.5 h-2.5 bg-brand-coral" data-id="element-2041" />
                  </div>
                </div>
                <div className="mt-auto pt-2 border-t border-slate-700 flex items-center justify-between" data-id="element-2042">
                  <div className="text-[9px] font-bold text-slate-400" data-id="element-2043">
                    Total upsell
                  </div>
                  <div className="text-[12px] font-bold text-brand-teal" data-id="element-2044">
                    $32,500
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Panel 3: Follow-up Activity */}
            <motion.div variants={fadeUp} className="bg-slate-800/60 rounded-lg border border-slate-700 p-3 flex flex-col" data-id="element-2045">
              <div className="flex items-center justify-between mb-3" data-id="element-2046">
                <div className="text-[10px] font-bold text-white" data-id="element-2047">
                  Follow-up Activity
                </div>
                <div className="text-[8px] text-slate-500" data-id="element-2048">Cedarwood Group</div>
              </div>
              <div className="relative flex-1 pl-3" data-id="element-2049">
                <div className="absolute left-0 top-1.5 bottom-1.5 w-px bg-slate-700" data-id="element-2050" />
                {[{
                label: 'Proposal sent',
                time: '2d ago',
                color: 'bg-brand-teal',
                done: true
              }, {
                label: 'Email opened',
                time: '2d ago',
                color: 'bg-brand-teal',
                done: true
              }, {
                label: 'Reminder scheduled',
                time: '1d ago',
                color: 'bg-amber-400',
                done: true
              }, {
                label: 'Client replied',
                time: '4h ago',
                color: 'bg-brand-coral',
                done: true,
                highlight: true
              }, {
                label: 'Call booked',
                time: 'pending',
                color: 'bg-slate-600',
                done: false
              }].map((step, i) => <motion.div key={i} initial={{
                opacity: 0,
                x: -8
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                duration: 0.4,
                delay: 0.8 + i * 0.15
              }} className="relative mb-3 last:mb-0" data-id="element-2051">
                    <div className={`absolute -left-[14px] top-0.5 w-2.5 h-2.5 rounded-full ${step.color} ${step.highlight ? 'ring-2 ring-brand-coral/30' : ''}`} data-id="element-2052" />
                    <div className={`text-[10px] font-bold ${step.highlight ? 'text-brand-coral' : step.done ? 'text-white' : 'text-slate-500'}`} data-id="element-2053">
                      {step.label}
                    </div>
                    <div className="text-[8px] text-slate-500" data-id="element-2054">{step.time}</div>
                  </motion.div>)}
              </div>
              <div className="mt-2 rounded bg-brand-coral/10 border border-brand-coral/30 p-2" data-id="element-2055">
                <div className="text-[9px] font-bold text-brand-coral mb-0.5" data-id="element-2056">
                  AI suggested next step
                </div>
                <div className="text-[9px] text-slate-300 leading-tight" data-id="element-2057">
                  Send 30-min call invite for Thu 2pm
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
        <motion.div variants={fadeUp} className="mt-8" data-id="element-2058">
          <a href="/designer" className="inline-flex items-center gap-2 text-brand-coral font-bold hover:text-white transition-colors text-base" data-id="element-2059">
            Open live demo <ArrowRightIcon className="w-5 h-5" data-id="element-2060" />
          </a>
        </motion.div>
      </motion.div>
    </div>,
  // 6) MARKET
  <div key="6" className="flex-1 flex flex-col justify-center px-8 md:px-20 max-w-6xl mx-auto w-full" data-id="element-2061">
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-2062">
        <motion.div variants={fadeUp} className="text-brand-coral text-sm font-bold uppercase tracking-[0.2em] mb-4" data-id="element-2063">
          Market
        </motion.div>
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-5xl font-bold mb-16 leading-tight max-w-3xl" data-id="element-2064">
          A category-defining wedge inside a $840B market.
        </motion.h2>
        <div className="flex flex-col md:flex-row gap-6" data-id="element-2065">
          {[{
          label: 'TAM',
          title: 'Corporate Offsites + MICE',
          val: '$840B',
          color: 'bg-slate-800 border-slate-700'
        }, {
          label: 'SAM',
          title: 'Hosted Retreat Segment',
          val: '$62B',
          color: 'bg-brand-navy border-brand-navy/50'
        }, {
          label: 'SOM',
          title: 'AI Experience Layer (2030)',
          val: '$4.1B',
          color: 'bg-brand-teal/20 border-brand-teal/50'
        }].map((item, i) => <motion.div key={i} variants={fadeUp} className={`flex-1 rounded-2xl border p-8 flex flex-col justify-between min-h-[240px] ${item.color}`} data-id="element-2066">
              <div data-id="element-2067">
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-2068">
                  {item.label}
                </div>
                <div className="text-lg font-medium text-white" data-id="element-2069">
                  {item.title}
                </div>
              </div>
              <div className="font-serif-display text-5xl font-bold text-white" data-id="element-2070">
                {item.val}
              </div>
            </motion.div>)}
        </div>
      </motion.div>
    </div>,
  // 7) WHY NOW
  <div key="7" className="flex-1 flex flex-col justify-center px-8 md:px-20 max-w-4xl mx-auto w-full" data-id="element-2071">
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-2072">
        <motion.div variants={fadeUp} className="text-brand-coral text-sm font-bold uppercase tracking-[0.2em] mb-4" data-id="element-2073">
          Why Now
        </motion.div>
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-5xl font-bold mb-16 leading-tight" data-id="element-2074">
          Three accelerating forces.
        </motion.h2>
        <div className="space-y-6" data-id="element-2075">
          {[{
          title: 'AI is mainstream',
          desc: 'Organizers expect intelligent generation, not blank canvases.'
        }, {
          title: 'Hybrid teams need offsites more than ever',
          desc: 'The annual retreat has replaced the daily office.'
        }, {
          title: 'Boards demand provable ROI',
          desc: 'Finance teams will no longer approve $100k events without data.'
        }].map((item, i) => <motion.div key={i} variants={fadeUp} className="flex items-start gap-6 p-6 rounded-2xl bg-white/5 border border-white/10" data-id="element-2076">
              <div className="w-12 h-12 rounded-full bg-brand-teal/20 text-brand-teal flex items-center justify-center shrink-0 font-serif-display text-2xl font-bold" data-id="element-2077">
                {i + 1}
              </div>
              <div data-id="element-2078">
                <h3 className="text-2xl font-bold mb-2" data-id="element-2079">{item.title}</h3>
                <p className="text-lg text-slate-400" data-id="element-2080">{item.desc}</p>
              </div>
            </motion.div>)}
        </div>
      </motion.div>
    </div>,
  // 8) BUSINESS MODEL
  <div key="8" className="flex-1 flex flex-col justify-center px-8 md:px-20 max-w-6xl mx-auto w-full" data-id="element-2081">
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-2082">
        <motion.div variants={fadeUp} className="text-brand-coral text-sm font-bold uppercase tracking-[0.2em] mb-4" data-id="element-2083">
          Business Model
        </motion.div>
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-5xl font-bold mb-16 leading-tight" data-id="element-2084">
          Three monetization layers, compounding.
        </motion.h2>
        <div className="grid md:grid-cols-3 gap-8" data-id="element-2085">
          {[{
          title: 'SaaS Subscriptions',
          price: '$499–$1,499',
          period: 'per event',
          desc: 'Direct to HR and People teams for internal events.'
        }, {
          title: 'Partner Licenses',
          price: '$20K–$80K',
          period: 'annual',
          desc: 'White-labeled commercial suite for Hotels and DMCs.'
        }, {
          title: 'Content Marketplace',
          price: '15%',
          period: 'take-rate',
          desc: 'Revenue share on licensed frameworks and expert modules.'
        }].map((item, i) => <motion.div key={i} variants={fadeUp} className="bg-white/5 border border-white/10 rounded-2xl p-8" data-id="element-2086">
              <h3 className="text-xl font-bold mb-6 text-slate-300" data-id="element-2087">
                {item.title}
              </h3>
              <div className="font-serif-display text-5xl font-bold text-white mb-2" data-id="element-2088">
                {item.price}
              </div>
              <div className="text-sm font-bold text-brand-teal uppercase tracking-wider mb-6" data-id="element-2089">
                {item.period}
              </div>
              <p className="text-slate-400 leading-relaxed" data-id="element-2090">{item.desc}</p>
            </motion.div>)}
        </div>
      </motion.div>
    </div>,
  // 9) GO-TO-MARKET
  <div key="9" className="flex-1 flex flex-col justify-center px-8 md:px-20 max-w-6xl mx-auto w-full" data-id="element-2091">
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-2092">
        <motion.div variants={fadeUp} className="text-brand-coral text-sm font-bold uppercase tracking-[0.2em] mb-4" data-id="element-2093">
          Go-To-Market
        </motion.div>
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-5xl font-bold mb-16 leading-tight max-w-4xl" data-id="element-2094">
          We sell through hotels — they sell us to clients.
        </motion.h2>
        <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-2095">
          <motion.div variants={fadeUp} className="w-full md:w-1/2 flex items-center justify-center" data-id="element-2096">
            <div className="relative flex items-center" data-id="element-2097">
              <div className="w-24 h-24 rounded-2xl bg-brand-navy border border-brand-teal/50 flex items-center justify-center z-10" data-id="element-2098">
                <Building2Icon className="w-10 h-10 text-brand-teal" data-id="element-2099" />
              </div>
              <div className="w-24 h-px bg-brand-teal/50" data-id="element-2100" />
              <div className="flex flex-col gap-4" data-id="element-2101">
                {[1, 2, 3].map(i => <div key={i} className="flex items-center" data-id="element-2102">
                    <div className="w-8 h-px bg-brand-teal/50" data-id="element-2103" />
                    <div className="w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center" data-id="element-2104">
                      <UsersIcon className="w-5 h-5 text-slate-300" data-id="element-2105" />
                    </div>
                  </div>)}
              </div>
            </div>
          </motion.div>
          <div className="w-full md:w-1/2 space-y-8" data-id="element-2106">
            {[{
            num: '01',
            title: 'Hotel/Resort Partners',
            desc: 'Our primary channel. They use Offsite+ to win group RFPs.'
          }, {
            num: '02',
            title: 'DMC Partners',
            desc: 'Secondary channel. They use us to add a premium experience layer.'
          }, {
            num: '03',
            title: 'Direct to HR',
            desc: 'Organic growth driven by attendees experiencing the product.'
          }].map((item, i) => <motion.div key={i} variants={fadeUp} className="flex gap-6" data-id="element-2107">
                <div className="font-serif-display text-3xl font-bold text-brand-coral" data-id="element-2108">
                  {item.num}
                </div>
                <div data-id="element-2109">
                  <h3 className="text-xl font-bold mb-2" data-id="element-2110">{item.title}</h3>
                  <p className="text-slate-400" data-id="element-2111">{item.desc}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </motion.div>
    </div>,
  // 10) TRACTION
  <div key="10" className="flex-1 flex flex-col justify-center px-8 md:px-20 max-w-6xl mx-auto w-full" data-id="element-2112">
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-2113">
        <motion.div variants={fadeUp} className="text-brand-coral text-sm font-bold uppercase tracking-[0.2em] mb-4" data-id="element-2114">
          Traction
        </motion.div>
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-5xl font-bold mb-4 leading-tight" data-id="element-2115">
          Battle-tested in executive education. <br data-id="element-2116" />
          Ready to spin off.
        </motion.h2>
        <motion.p variants={fadeUp} className="text-base text-slate-400 mb-12 max-w-3xl leading-relaxed" data-id="element-2117">
          We've delivered the core platform across executive education programs
          at top business schools — proving the design, content, and outcomes
          engine in the most demanding environment. Now we're commercializing.
        </motion.p>
        <div className="grid md:grid-cols-2 gap-8" data-id="element-2118">
          <div className="grid grid-cols-2 gap-4" data-id="element-2119">
            {[{
            val: '15',
            label: 'Events Delivered'
          }, {
            val: '12',
            label: 'Partner Properties'
          }, {
            val: '94%',
            label: 'Attendee NPS'
          }, {
            val: '$2.4M',
            label: 'Committed Pipeline'
          }].map((item, i) => <motion.div key={i} variants={fadeUp} className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col justify-center items-center text-center" data-id="element-2120">
                <div className="font-serif-display text-4xl font-bold text-white mb-2" data-id="element-2121">
                  {item.val}
                </div>
                <div className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-2122">
                  {item.label}
                </div>
              </motion.div>)}
          </div>
          <motion.div variants={fadeUp} className="bg-white/5 border border-white/10 rounded-2xl p-8 flex flex-col" data-id="element-2123">
            <div className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-2124">
              Projected MRR — Next 6 Months
            </div>
            <div className="text-[11px] text-slate-500 mb-6 italic" data-id="element-2125">
              Pre-revenue model · pricing rollout Q1 2026
            </div>
            <div className="flex-1 flex items-end gap-2" data-id="element-2126">
              {[15, 25, 30, 45, 70, 100].map((h, i) => <motion.div key={i} initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: `${h}%`,
              opacity: 1
            }} transition={{
              duration: 0.8,
              delay: 0.5 + i * 0.1,
              ease: [0.22, 1, 0.36, 1]
            }} className="flex-1 bg-brand-teal/80 rounded-t-sm origin-bottom" data-id="element-2127" />)}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>,
  // 11) COMPETITION
  <div key="11" className="flex-1 flex flex-col justify-center px-8 md:px-20 max-w-6xl mx-auto w-full" data-id="element-2128">
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-2129">
        <motion.div variants={fadeUp} className="text-brand-coral text-sm font-bold uppercase tracking-[0.2em] mb-4" data-id="element-2130">
          Competition
        </motion.div>
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-5xl font-bold mb-12 leading-tight" data-id="element-2131">
          Why we win.
        </motion.h2>
        <motion.div variants={fadeUp} className="overflow-x-auto" data-id="element-2132">
          <table className="w-full text-left border-collapse" data-id="element-2133">
            <thead data-id="element-2134">
              <tr data-id="element-2135">
                <th className="p-4 text-sm font-bold text-slate-400 uppercase tracking-wider w-1/4" data-id="element-2136">
                  Capabilities
                </th>
                <th className="p-4 text-center text-sm font-bold text-slate-400 w-1/5" data-id="element-2137">
                  DIY
                </th>
                <th className="p-4 text-center text-sm font-bold text-slate-400 w-1/5" data-id="element-2138">
                  Cvent
                </th>
                <th className="p-4 text-center text-sm font-bold text-slate-400 w-1/5" data-id="element-2139">
                  Generic Apps
                </th>
                <th className="p-4 text-center text-lg font-bold text-brand-teal w-1/5 border-x border-t border-brand-teal/30 bg-brand-teal/5 rounded-t-xl" data-id="element-2140">
                  Offsite+
                </th>
              </tr>
            </thead>
            <tbody data-id="element-2141">
              {[{
              name: 'AI Agenda Generation',
              diy: false,
              cvent: false,
              generic: false,
              offsite: true
            }, {
              name: 'Branded Event Hub',
              diy: false,
              cvent: true,
              generic: true,
              offsite: true
            }, {
              name: 'Outcomes Report',
              diy: false,
              cvent: 'Partial',
              generic: false,
              offsite: true
            }, {
              name: 'Content Library',
              diy: false,
              cvent: false,
              generic: false,
              offsite: true
            }, {
              name: 'Partner Monetization',
              diy: false,
              cvent: false,
              generic: false,
              offsite: true
            }].map((row, i) => <tr key={i} className="border-t border-white/10" data-id="element-2142">
                  <td className="p-4 text-sm font-medium text-white" data-id="element-2143">
                    {row.name}
                  </td>
                  <td className="p-4 text-center text-slate-500" data-id="element-2144">
                    {row.diy === true ? <CheckCircle2Icon className="w-5 h-5 mx-auto text-emerald-500" data-id="element-2145" /> : row.diy || '—'}
                  </td>
                  <td className="p-4 text-center text-slate-500" data-id="element-2146">
                    {row.cvent === true ? <CheckCircle2Icon className="w-5 h-5 mx-auto text-emerald-500" data-id="element-2147" /> : row.cvent || '—'}
                  </td>
                  <td className="p-4 text-center text-slate-500" data-id="element-2148">
                    {row.generic === true ? <CheckCircle2Icon className="w-5 h-5 mx-auto text-emerald-500" data-id="element-2149" /> : row.generic || '—'}
                  </td>
                  <td className="p-4 text-center border-x border-brand-teal/30 bg-brand-teal/5" data-id="element-2150">
                    <CheckCircle2Icon className="w-5 h-5 mx-auto text-brand-teal" data-id="element-2151" />
                  </td>
                </tr>)}
              <tr data-id="element-2152">
                <td className="p-0" data-id="element-2153"></td>
                <td className="p-0" data-id="element-2154"></td>
                <td className="p-0" data-id="element-2155"></td>
                <td className="p-0" data-id="element-2156"></td>
                <td className="p-0 border-x border-b border-brand-teal/30 bg-brand-teal/5 rounded-b-xl h-4" data-id="element-2157"></td>
              </tr>
            </tbody>
          </table>
        </motion.div>
      </motion.div>
    </div>,
  // 12) TEAM
  <div key="12" className="flex-1 flex flex-col justify-center px-8 md:px-20 max-w-6xl mx-auto w-full" data-id="element-2158">
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-2159">
        <motion.div variants={fadeUp} className="text-brand-coral text-sm font-bold uppercase tracking-[0.2em] mb-4" data-id="element-2160">
          Team
        </motion.div>
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-5xl font-bold mb-6 leading-tight" data-id="element-2161">
          150 collective years of expertise to make this work.
        </motion.h2>
        <motion.p variants={fadeUp} className="text-base text-slate-400 mb-16 max-w-3xl leading-relaxed" data-id="element-2162">
          A founding team purpose-built for this category — fusing world-class
          executive education with hospitality operations and AI engineering.
        </motion.p>
        <div className="grid md:grid-cols-4 gap-6 mb-12" data-id="element-2163">
          {[{
          years: '80',
          field: 'Executive Education',
          detail: 'at top-ranked business schools',
          accent: 'text-brand-coral'
        }, {
          years: '30',
          field: 'Hospitality',
          detail: 'group sales & resort operations',
          accent: 'text-brand-teal'
        }, {
          years: '30',
          field: 'Technology',
          detail: 'consumer & SaaS platforms',
          accent: 'text-amber-400'
        }, {
          years: '10',
          field: 'Applied AI',
          detail: 'LLM systems & agent design',
          accent: 'text-blue-400'
        }].map((item, i) => <motion.div key={i} variants={fadeUp} className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col" data-id="element-2164">
              <div className="flex items-baseline gap-1 mb-4" data-id="element-2165">
                <div className={`font-serif-display text-5xl font-bold ${item.accent}`} data-id="element-2166">
                  {item.years}
                </div>
                <div className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-2167">
                  years
                </div>
              </div>
              <div className="text-base font-bold text-white mb-1" data-id="element-2168">
                {item.field}
              </div>
              <div className="text-xs text-slate-400 leading-relaxed" data-id="element-2169">
                {item.detail}
              </div>
            </motion.div>)}
        </div>
      </motion.div>
    </div>,
  // 13) ROADMAP
  <div key="13" className="flex-1 flex flex-col justify-center px-8 md:px-20 max-w-6xl mx-auto w-full" data-id="element-2170">
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-2171">
        <motion.div variants={fadeUp} className="text-brand-coral text-sm font-bold uppercase tracking-[0.2em] mb-4" data-id="element-2172">
          24-Month Plan
        </motion.div>
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-5xl font-bold mb-20 leading-tight" data-id="element-2173">
          The path to Series A.
        </motion.h2>
        <div className="relative" data-id="element-2174">
          <div className="absolute top-6 left-0 w-full h-px bg-white/20" data-id="element-2175" />
          <div className="grid grid-cols-4 gap-6" data-id="element-2176">
            {[{
            q: 'Q3 2026',
            title: '100 Events',
            desc: 'Prove the core loop'
          }, {
            q: 'Q1 2027',
            title: 'Marketplace Launch',
            desc: 'First 50 content partners'
          }, {
            q: 'Q4 2027',
            title: '500 Events',
            desc: 'Series A milestones hit'
          }, {
            q: 'Q2 2028',
            title: 'Global Network',
            desc: 'Expansion to EMEA/APAC'
          }].map((item, i) => <motion.div key={i} variants={fadeUp} className="relative pt-12" data-id="element-2177">
                <div className="absolute top-5 left-0 w-3 h-3 rounded-full bg-brand-teal shadow-[0_0_15px_rgba(14,165,164,0.5)]" data-id="element-2178" />
                <div className="text-sm font-bold text-brand-coral mb-2" data-id="element-2179">
                  {item.q}
                </div>
                <h3 className="text-xl font-bold text-white mb-2" data-id="element-2180">
                  {item.title}
                </h3>
                <p className="text-sm text-slate-400" data-id="element-2181">{item.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </motion.div>
    </div>,
  // 14) THE ASK
  <div key="14" className="flex-1 flex flex-col justify-center px-8 md:px-20 max-w-5xl mx-auto w-full" data-id="element-2182">
      <motion.div variants={stagger} initial="initial" animate="animate" data-id="element-2183">
        <motion.div variants={fadeUp} className="text-brand-coral text-sm font-bold uppercase tracking-[0.2em] mb-4" data-id="element-2184">
          The Ask
        </motion.div>
        <motion.h2 variants={fadeUp} className="font-serif-display text-5xl md:text-7xl font-bold mb-10 leading-tight" data-id="element-2185">
          Raising $2M <br data-id="element-2186" />
          <span className="text-slate-500" data-id="element-2187">at $12M post.</span>
        </motion.h2>
        <motion.div variants={fadeUp} className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6 mb-10 p-5 rounded-2xl bg-gradient-to-r from-brand-teal/15 via-brand-teal/5 to-transparent border border-brand-teal/30" data-id="element-2188">
          <div className="flex items-baseline gap-2" data-id="element-2189">
            <div className="font-serif-display text-4xl md:text-5xl font-bold text-brand-teal" data-id="element-2190">
              $850K
            </div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-brand-teal/80" data-id="element-2191">
              soft-circled
            </div>
          </div>
          <div className="h-px md:h-10 md:w-px bg-white/15 flex-shrink-0" data-id="element-2192" />
          <p className="text-sm md:text-base text-slate-300 leading-relaxed" data-id="element-2193">
            ~43% of the round committed by strategic angels and hospitality
            operators. Closing the remaining $1.15M with lead investors now.
          </p>
        </motion.div>
        <motion.div variants={fadeUp} className="bg-white/5 border border-white/10 rounded-2xl p-8" data-id="element-2194">
          <h3 className="text-lg font-bold mb-8" data-id="element-2195">Use of Funds</h3>
          <div className="flex h-12 rounded-lg overflow-hidden mb-6" data-id="element-2196">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: '50%'
          }} transition={{
            duration: 0.9,
            delay: 0.4,
            ease: [0.22, 1, 0.36, 1]
          }} className="bg-brand-teal flex items-center px-4 text-xs font-bold text-[#0A0F1F] whitespace-nowrap overflow-hidden" data-id="element-2197">
              Product 50%
            </motion.div>
            <motion.div initial={{
            width: 0
          }} animate={{
            width: '30%'
          }} transition={{
            duration: 0.7,
            delay: 0.9,
            ease: [0.22, 1, 0.36, 1]
          }} className="bg-brand-coral flex items-center px-4 text-xs font-bold text-white whitespace-nowrap overflow-hidden" data-id="element-2198">
              GTM 30%
            </motion.div>
            <motion.div initial={{
            width: 0
          }} animate={{
            width: '15%'
          }} transition={{
            duration: 0.6,
            delay: 1.3,
            ease: [0.22, 1, 0.36, 1]
          }} className="bg-amber-500 flex items-center px-4 text-xs font-bold text-[#0A0F1F] whitespace-nowrap overflow-hidden" data-id="element-2199">
              Content 15%
            </motion.div>
            <motion.div initial={{
            width: 0
          }} animate={{
            width: '5%'
          }} transition={{
            duration: 0.5,
            delay: 1.7,
            ease: [0.22, 1, 0.36, 1]
          }} className="bg-slate-600 flex items-center px-2 text-xs font-bold text-white whitespace-nowrap overflow-hidden" data-id="element-2200">
              5%
            </motion.div>
          </div>
          <div className="grid grid-cols-4 gap-4 text-sm text-slate-400" data-id="element-2201">
            <div data-id="element-2202">Engineering & AI R&D</div>
            <div data-id="element-2203">Sales & Partner Success</div>
            <div data-id="element-2204">Licensing & Faculty</div>
            <div data-id="element-2205">Ops & Legal</div>
          </div>
        </motion.div>
      </motion.div>
    </div>,
  // 15) CLOSING
  <div key="15" className="flex-1 flex flex-col items-center justify-center relative px-6 text-center" data-id="element-2206">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,107,74,0.1)_0%,transparent_50%)] pointer-events-none" data-id="element-2207" />
      <motion.div variants={stagger} initial="initial" animate="animate" className="z-10 max-w-4xl" data-id="element-2208">
        <motion.h2 variants={fadeUp} className="font-serif-display text-4xl md:text-6xl font-bold mb-16 leading-tight text-white" data-id="element-2209">
          "Make every group event the most transformative thing your team did
          this year."
        </motion.h2>
        <motion.div variants={fadeUp} className="flex flex-col items-center gap-4" data-id="element-2210">
          <div className="font-bold text-2xl tracking-tight flex items-start leading-none mb-2" data-id="element-2211">
            <span className="text-white" data-id="element-2212">Offsite</span>
            <span className="text-brand-coral font-black relative -top-0.5 ml-0.5" data-id="element-2213">
              +
            </span>
          </div>
          <a href="mailto:invest@offsiteplus.com" className="text-brand-teal hover:text-white transition-colors font-medium" data-id="element-2214">
            invest@offsiteplus.com
          </a>
        </motion.div>
      </motion.div>
    </div>];
  return <DeckShell slides={slides} theme="dark" brandLabel="Investor Deck · v2026" data-id="element-2215" />;
}